

import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DEFAULT_LANG } from '@app/core/i18n/supported-langs';
import { LogService } from '@app/core/log/log.service';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { BrandingConfig, DEFAULT_BRANDING, PartialBrandingConfig } from './branding.models';
import { BRANDINGS } from './brandings.constants';

const ORIGIN_APP_PARAM = 'gig_originSite';

@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly document = inject(DOCUMENT);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map(({ lang }) => lang)),
    { initialValue: this.translate.getCurrentLang() ?? DEFAULT_LANG },
  );

  private readonly _branding = signal<BrandingConfig>(DEFAULT_BRANDING);
  readonly branding = computed<BrandingConfig>(() => {
    const branding = this._branding();
    const localizedLogoUrl = branding.logoUrls?.[this.currentLang()];

    return {
      ...branding,
      logoUrl: localizedLogoUrl ?? branding.logoUrl,
    };
  });

  constructor() {
    this.applyCssVariables(DEFAULT_BRANDING);
  }

  applyFromQuery(search: string = this.document.defaultView?.location.search ?? ''): void {
    LogService.info(`Apply branding from query`);
    const params = new URLSearchParams(search);
    const originApp = params.get(ORIGIN_APP_PARAM);
    if (!originApp) return;
    this.applyOriginApp(originApp);
  }

  private applyOriginApp(originSite: string): void {
    const override = BRANDINGS[originSite];
    if (!override) {
      LogService.warn(`Branding not found para originSite="${originSite}"`);
      return;
    }
    const merged = this.merge(DEFAULT_BRANDING, override);
    this._branding.set(merged);
    this.applyCssVariables(merged);

  }

  private merge(base: BrandingConfig, override: PartialBrandingConfig): BrandingConfig {
    const usesFixedLogo = override.logoUrl !== undefined && override.logoUrls === undefined;

    return {
      companyName: override.companyName ?? base.companyName,
      logoUrl: override.logoUrl ?? base.logoUrl,
      logoUrls: usesFixedLogo ? undefined : (override.logoUrls ?? base.logoUrls),
      logoAlt: override.logoAlt ?? base.logoAlt,
      colors: { ...base.colors, ...override.colors },
      footer: { ...base.footer, ...override.footer },
    };
  }

  private applyCssVariables(branding: BrandingConfig): void {
    const root = this.document.documentElement;
    root.style.setProperty('--brand-header', branding.colors.header);
    root.style.setProperty('--brand-primary', branding.colors.primary);
    root.style.setProperty('--brand-primary-dark', branding.colors.primaryDark);
    root.style.setProperty('--brand-primary-light', branding.colors.primaryLight);
  }
}

